import { useState, useEffect, useCallback } from 'react';
import { Rss, ExternalLink } from 'lucide-react';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { dataService } from '../services/dataService';

interface NewsItem {
  title: string;
  source: { name: string };
  publishedAt: string;
  url: string;
  description?: string;
  urlToImage?: string;
  category?: string;
}

const DashboardNewsWidget = () => {
  const { t, language } = useLanguage();
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [dataSource, setDataSource] = useState<'newsapi' | 'backup' | 'unknown'>('unknown');

  const fetchNews = useCallback(async (isBackgroundRefresh = false) => {
    if (!isBackgroundRefresh) {
      setLoading(true);
    }
    try {
      console.log('🔄 开始获取AI新闻数据...');
      const items = await dataService.getAINews(language);
      console.log('✅ 获取到新闻数据:', items.length, '条');
      
      if (items.length > 0) {
        const hasRealUrls = items.some((item: NewsItem) => item.url && !item.url.includes('example.com') && item.url !== '#');
        setDataSource(hasRealUrls ? 'newsapi' : 'backup');
        setNewsItems(items.slice(0, 5));
      } else {
        setDataSource('backup');
        setNewsItems([]);
      }
      
    } catch (error) {
      console.error("❌ 获取AI新闻失败:", error);
      setDataSource('backup');
      setNewsItems([]);
    } finally {
      if (!isBackgroundRefresh) {
        setLoading(false);
      }
    }
  }, [language]);

  useEffect(() => {
    fetchNews(); // 首次加载

    const intervalId = setInterval(() => fetchNews(true), 3600 * 1000); // 每小时后台刷新一次

    return () => clearInterval(intervalId); // 组件卸载时清除定时器
  }, [fetchNews]);

  const timeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    let interval = seconds / 31536000;
    if (interval > 1) return Math.floor(interval) + " " + t('time.yearsAgo');
    
    interval = seconds / 2592000;
    if (interval > 1) return Math.floor(interval) + " " + t('time.monthsAgo');
    
    interval = seconds / 86400;
    if (interval > 1) return Math.floor(interval) + " " + t('time.daysAgo');
    
    interval = seconds / 3600;
    if (interval > 1) return Math.floor(interval) + " " + t('time.hoursAgo');
    
    interval = seconds / 60;
    if (interval > 1) return Math.floor(interval) + " " + t('time.minutesAgo');
    
    return Math.floor(seconds) + " " + t('time.secondsAgo');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    // 根据当前语言格式化日期
    const locale = language === 'zh' ? 'zh-CN' : 'en-US';
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  return (
    <motion.div 
      className="h-full flex flex-col"
    >
      <h3 className="font-playfair text-sm font-semibold text-[#2c1810] mb-3 flex items-center justify-between">
        <div className="flex items-center">
          <Rss className="w-4 h-4 mr-2 text-[#2c1810]" />
          {t('news.latestAI')}
        </div>
        {!loading && (
          <div className={`font-merriweather text-xs px-2 py-1 border border-[#2c1810]/20 ${
            dataSource === 'newsapi' 
              ? 'bg-[#2c1810]/5 text-[#2c1810]' 
              : 'bg-[#2c1810]/5 text-[#2c1810]'
          }`}>
            {dataSource === 'newsapi' ? t('news.realTime') : t('news.featured')}
          </div>
        )}
      </h3>
      <div className="flex-1 space-y-3 overflow-y-auto pr-2">
        {loading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="animate-pulse flex space-x-4">
                <div className="flex-1 space-y-2 py-1">
                  <div className="h-2 bg-[#2c1810]/10 rounded"></div>
                  <div className="h-2 bg-[#2c1810]/10 rounded w-5/6"></div>
                  <div className="h-2 bg-[#2c1810]/10 rounded w-1/4"></div>
                </div>
              </div>
            ))}
          </div>
        ) : newsItems.length > 0 ? (
          newsItems.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <a 
                href={item.url && item.url !== '#' ? item.url : undefined} 
                target="_blank" 
                rel="noopener noreferrer" 
                className={`block p-2 group ${item.url && item.url !== '#' ? 'hover:bg-[#2c1810]/5 cursor-pointer' : 'cursor-default'}`}
                onClick={item.url === '#' ? (e) => e.preventDefault() : undefined}
              >
                <p className="font-playfair text-xs font-medium text-[#2c1810] group-hover:text-[#2c1810]/80 mb-1 leading-snug">
                  {item.title.length > 80 ? item.title.substring(0, 80) + '...' : item.title}
                </p>
                {item.description && (
                  <p className="font-merriweather text-xs text-[#2c1810]/80 mb-2">
                    {item.description.length > 60 ? item.description.substring(0, 60) + '...' : item.description}
                  </p>
                )}
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-xs text-[#2c1810]/60">
                    <span className="font-merriweather">{item.source.name} · {timeAgo(item.publishedAt)}</span>
                    {item.url && item.url !== '#' && (
                      <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    )}
                  </div>
                  <div className="font-merriweather text-xs text-[#2c1810]/60">
                    {formatDate(item.publishedAt)}
                  </div>
                </div>
              </a>
            </motion.div>
          ))
        ) : (
          <div className="text-center text-sm text-[#2c1810]/60 py-10">
            <p className="font-merriweather">{t('news.noNewsFound')}</p>
            <p className="font-merriweather text-xs mt-2">{t('news.checkAPIKey')}</p>
          </div>
        )}
      </div>
      <div className="mt-3 text-center">
        <a href="/news" className="font-merriweather text-xs font-medium text-[#2c1810] hover:text-[#2c1810]/80 flex items-center justify-center space-x-1">
          <span>{t('news.viewMore')}</span>
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    </motion.div>
  );
};

export default DashboardNewsWidget; 